/**
 * Acciones de la ficha de inquilino: editar, marcar inactivo, borrar y anonimizar.
 *
 * ACTIVO / INACTIVO NO ES UN CAMPO DEL INQUILINO, y no lo es a propósito: lo dice su contrato.
 * Si fuera una casilla suelta, rescindir un contrato y olvidarse de desmarcarla dejaría a
 * alguien «activo» en una habitación que hoy ocupa otro, y la ocupación del inmueble diría una
 * cosa y la ficha otra. Así que el estado se DERIVA (`currentLease`) y la acción de cambiarlo
 * es la que de verdad lo cambia: terminar la estancia.
 *
 * BORRAR Y ANONIMIZAR SON COSAS DISTINTAS, y la interfaz tiene que distinguirlas:
 *
 *  · BORRAR se lo lleva TODO, en cualquier inquilino —también con contrato vigente—: la ficha,
 *    sus contratos, cargos, cobros y documentos (`services/tenantPurge.ts`). Es irreversible, así
 *    que enseña antes lo que va a borrar y pide escribir el nombre.
 *  · ANONIMIZAR tampoco se deshace, pero NO borra: sustituye la identidad y conserva contratos y
 *    pagos, porque respaldan ejercicios ya declarados. Es la respuesta al derecho de supresión
 *    cuando hay obligaciones contables por medio. Se niega con un 409 si hay contrato ACTIVO; la
 *    comprobación vive en el servidor y aquí solo se adelanta.
 *
 * Los DADOS DE BAJA de antes salen en «Todos» y su ficha se abre, pero solo para lo que les
 * queda: REACTIVARLOS o borrarlos.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TenantDetailResponse } from '@naar/shared';
import { TerminateDialog } from '../leases/LeaseActions';
import { ApiError, api, queryKeys } from '../../api/client';
import { useCan } from '../../auth/AuthContext';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { toast } from '../../components/feedback/Toaster';
import { formatEurCents } from '../../lib/format';
import { TenantFormDrawer } from './TenantFormDrawer';
import { AssignRoomDrawer } from './AssignRoomDrawer';
import styles from './TenantActions.module.css';

type Dialog = 'borrar' | 'anonimizar' | 'inactivo' | null;

export function TenantActions({ tenant }: { tenant: TenantDetailResponse }) {
  const canUpdate = useCan('tenant:update');
  const canDelete = useCan('tenant:delete');
  const canCreateLease = useCan('lease:create');
  const canTerminate = useCan('lease:terminate');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenant.id) });
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  }

  const hasActiveLease = tenant.currentLease !== null;
  const isDeleted = tenant.deletedAt !== null;

  const restore = useMutation({
    mutationFn: () => api.restoreTenant(tenant.id),
    onSuccess: () => {
      refresh();
      toast.success(`${tenant.fullName} vuelve a estar de alta.`);
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido reactivar.'),
  });

  const anonymize = useMutation({
    mutationFn: () => api.anonymizeTenant(tenant.id),
    onSuccess: () => {
      refresh();
      toast.success('Datos personales anonimizados. Los contratos y pagos se conservan.');
      setDialog(null);
      // Se vuelve al listado: la ficha que había abierta ya no describe a nadie.
      navigate('/inquilinos', { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido anonimizar.');
      setDialog(null);
    },
  });

  return (
    <>
      {/*
        Dónde vive. Es la acción principal de la ficha de alguien recién dado de alta: un
        inquilino sin habitación no genera cobros ni cuenta para la ocupación de nada.
      */}
      {/* Con un contrato en borrador ya tiene habitación: lo que falta es activarlo (Contratos). */}
      {canCreateLease && !isDeleted && !hasActiveLease && !tenant.draftLease && (
        <Button size="sm" onClick={() => setAssigning(true)}>
          <Icon name="plus" size={14} strokeWidth={2.4} />
          Asignar habitación
        </Button>
      )}

      {/* Un dado de baja ya no se edita: se reactiva antes, o se borra. */}
      {canDelete && isDeleted && !tenant.anonymized && (
        <Button variant="secondary" size="sm" onClick={() => restore.mutate()} disabled={restore.isPending}>
          Reactivar
        </Button>
      )}

      {canUpdate && !isDeleted && (
        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
          <Icon name="edit" size={14} strokeWidth={2} />
          Editar
        </Button>
      )}

      {/*
        Pasar a inactivo = terminar la estancia. Solo aparece cuando hay algo que terminar; si
        ya no vive en ningún sitio, el inquilino YA está inactivo y el botón no tendría efecto.
      */}
      {canTerminate && hasActiveLease && (
        <Button variant="secondary" size="sm" onClick={() => setDialog('inactivo')}>
          <Icon name="transfer" size={14} strokeWidth={2} />
          Marcar como inactivo
        </Button>
      )}

      {/* Borrar del todo, también con contrato vigente: se va con todo lo suyo. */}
      {canDelete && (
        <Button variant="danger" size="sm" onClick={() => setDialog('borrar')}>
          <Icon name="trash" size={14} strokeWidth={2} />
          Borrar inquilino
        </Button>
      )}

      {canDelete && !tenant.anonymized && (
        <button
          type="button"
          className={styles.gdprLink}
          onClick={() => setDialog('anonimizar')}
          disabled={hasActiveLease}
          title={
            hasActiveLease
              ? 'Tiene un contrato vigente. Recíndelo antes de anonimizar.'
              : 'Derecho de supresión: sustituye los datos personales y conserva la contabilidad.'
          }
        >
          Anonimizar (RGPD)
        </button>
      )}

      <AssignRoomDrawer
        open={assigning}
        onClose={() => setAssigning(false)}
        tenantId={tenant.id}
        tenantName={tenant.fullName}
      />

      <TenantFormDrawer open={editing} tenant={tenant} onClose={() => setEditing(false)} />

      {dialog === 'inactivo' && tenant.currentLease && (
        <TerminateDialog lease={tenant.currentLease} onClose={() => setDialog(null)} />
      )}

      {dialog === 'borrar' && <PurgeDialog tenant={tenant} onClose={() => setDialog(null)} />}

      {dialog === 'anonimizar' && (
        <ConfirmDialog
          title="Anonimizar los datos personales"
          body="Esto NO se puede deshacer. El nombre, el documento, el correo y el teléfono se sustituyen por un identificador. Los contratos, los cargos y los pagos siguen existiendo: respaldan ejercicios ya declarados y no se pueden borrar."
          confirmLabel="Anonimizar"
          tone="danger"
          /**
           * Confirmación fuerte, como en el borrado de inmueble: es irreversible, y aquí lo
           * que desaparece es la identidad de una persona real.
           */
          requireText={tenant.fullName}
          requireTextHint={
            <>
              Para confirmar, escribe el nombre: <code>{tenant.fullName}</code>
            </>
          }
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await anonymize.mutateAsync();
          }}
        />
      )}
    </>
  );
}

/** «1 cobro», «3 cobros». */
function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * Confirmación del borrado. Enseña lo que se va a llevar por delante —contado por el SERVIDOR,
 * el mismo cálculo que hará al borrar— y lo que lo impide, y no deja confirmar hasta que se
 * escribe el nombre.
 */
function PurgeDialog({ tenant, onClose }: { tenant: TenantDetailResponse; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const preview = useQuery({
    queryKey: queryKeys.tenantPurgePreview(tenant.id),
    queryFn: () => api.tenantPurgePreview(tenant.id),
    staleTime: 0,
  });

  const purge = useMutation({
    mutationFn: (typed: string) => api.purgeTenant(tenant.id, typed),
    onSuccess: (result) => {
      /*
       * Al listado, y su ficha fuera de la caché: volver a pedirla daría «Ese inquilino no
       * existe». Lo demás se refresca ENTERO: el inquilino salía en muchas más pantallas que el
       * listado —pisos y ocupación, cobros, pendientes, suministros, el panel—.
       */
      navigate('/inquilinos', { replace: true });
      queryClient.removeQueries({ queryKey: queryKeys.tenant(tenant.id) });
      void queryClient.invalidateQueries();
      const bank =
        result.bankMovements > 0
          ? ` ${plural(result.bankMovements, 'movimiento del banco vuelve', 'movimientos del banco vuelven')} a estar sin identificar en Ingresos y gastos.`
          : '';
      toast.success(`${result.fullName} borrado con todo lo suyo.${bank}`);
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido borrar al inquilino.'),
  });

  const blocked = (preview.data?.blockers.length ?? 0) > 0;
  const lease = tenant.currentLease;

  return (
    <ConfirmDialog
      title={`Borrar a ${tenant.fullName}`}
      body={`Esto NO se puede deshacer. Se borra la ficha con todo lo suyo: contratos${
        lease ? ` (también el vigente de ${lease.unit.code}, que queda libre)` : ''
      }, cargos, cobros y documentos.`}
      confirmLabel="Borrar inquilino"
      tone="danger"
      /* Hay que escribirlo: se lleva por delante todo, y un clic de más no basta. */
      requireText={tenant.fullName}
      requireTextHint={
        <>
          Para confirmar, escribe su nombre: <code>{tenant.fullName}</code>
        </>
      }
      confirmDisabled={!preview.data || blocked}
      onCancel={onClose}
      onConfirm={async (typed) => {
        await purge.mutateAsync(typed);
      }}
    >
      {preview.isPending ? (
        <p className={styles.purgeList}>Contando lo que se va a borrar…</p>
      ) : preview.isError ? (
        <p className={styles.purgeBlocked}>
          {preview.error instanceof Error ? preview.error.message : 'No se ha podido calcular qué se borra.'}
        </p>
      ) : (
        <>
          <ul className={styles.purgeList}>
            <li>
              <strong>{plural(preview.data.leases, 'contrato', 'contratos')}</strong> con{' '}
              {plural(preview.data.rentCharges, 'cargo', 'cargos')} de renta
            </li>
            <li>
              <strong>{plural(preview.data.payments, 'cobro', 'cobros')}</strong>
              {preview.data.payments > 0 && <> por {formatEurCents(preview.data.paymentsTotal)}</>}
            </li>
            <li>
              <strong>{plural(preview.data.documents, 'documento', 'documentos')}</strong>, también el
              fichero
            </li>
            {preview.data.notes + preview.data.alerts > 0 && (
              <li>
                {plural(preview.data.notes, 'nota', 'notas')} y{' '}
                {plural(preview.data.alerts, 'alerta', 'alertas')}
              </li>
            )}
            {preview.data.bankMovements > 0 && (
              <li>
                {plural(preview.data.bankMovements, 'movimiento', 'movimientos')} del banco{' '}
                <strong>no se borran</strong>: vuelven a estar sin identificar
              </li>
            )}
          </ul>
          {preview.data.blockers.map((blocker) => (
            <p key={blocker} className={styles.purgeBlocked}>
              {blocker}
            </p>
          ))}
        </>
      )}
    </ConfirmDialog>
  );
}
